/**
 * @fileoverview The first-run welcome — a carousel in a dialog, over the
 * dashboard.
 *
 * This replaces `/founding/welcome`, a full page that sat between onboarding
 * and the app. Three things were wrong with that shape and all three are why
 * this is a popup instead.
 *
 * * **A page between onboarding and the dashboard is another wall.** Someone
 *   who has just filled in four screens does not want a fifth. Over the
 *   dashboard the app is already visible behind it, so closing it is arriving
 *   rather than navigating.
 * * **The welcome was founding-only, and the useful part is not.** Add your
 *   students, put your week in, capture the lesson, send it — that is what ANY
 *   new teacher needs. Only the first slide is about the cohort, so a founding
 *   member gets it and everyone else starts at the same walkthrough.
 * * **The slide count is derived, never typed.** The old page's heading said
 *   "Three things to do first" above a list whose middle item was gated on
 *   `SCHEDULING_ENABLED` — with the flag off it promised three and showed two.
 *   Nothing here states a count that isn't `slides.length`, and no eyebrow
 *   states an ordinal, so a gated-out step can't leave the copy counting wrong.
 *
 * **Exactly one slide navigates, and it is the last one.** Every step slide
 * used to carry its own "Add a student" link, which is a walkthrough that ends
 * itself: the first slide's own call to action closed the dialog and took you
 * away, so the three slides explaining what to do next were unreachable unless
 * you happened to press Next instead. The steps now TEACH — what the thing is,
 * what Hans does with it, what it costs you — and the single action waits at
 * the end, where a person has read the walkthrough and is ready to act on it.
 * The dialog's own close control is still the way out for anyone who isn't.
 *
 * **Opening it is the caller's job** (see `Dashboard`): this component only
 * knows how to be open. That keeps the trigger — arriving from onboarding, or
 * `?welcome=1` — in one place, next to the thing that clears it.
 *
 * It moves between slides with `lib/onboardingMotion`'s shared variants rather
 * than the embla primitive in `ui/carousel`, for the same reason that module
 * exists: this is the last leg of the arrival that enrolment and onboarding
 * begin, and a third transition would make one sequence look like three
 * products. The practical dividend is that the progression is testable —
 * embla measures layout, and jsdom has none, so a slide index driven by it
 * cannot be asserted at all.
 */

import { useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Loader2,
  Mail,
  MessageSquare,
  Mic,
  Sparkles,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

import api, { getErrorMessage } from "@/lib/api";
import FoundingMedal from "@/components/FoundingMedal";
import { AuthContext } from "@/App";
import { SCHEDULING_ENABLED } from "@/lib/featureFlags";
import { formatComplimentaryUntil, isFoundingMember } from "@/lib/founding";
import { slideVariants } from "@/lib/onboardingMotion";
import { firstName } from "@/lib/userName";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

/** Where the closing button goes. `?add=1` opens the roster's add dialog. */
const ADD_STUDENT_TO = "/students?add=1";

/**
 * The cohort's one research question.
 *
 * It used to sit on the sign-up form, which is the worst possible place for it:
 * an essay box between a personal invitation and the account it promised. Here
 * the account already exists, so declining costs nothing — and it posts to the
 * same feedback table the Share feedback button writes to, which is where these
 * actually get read.
 *
 * `page` stays `/founding/welcome` rather than the dashboard path this now
 * renders over. The field identifies the SURFACE for whoever reads the table,
 * and "the founding welcome question" is a more useful answer than "somebody
 * was on the dashboard" — which is what every Share feedback row already says.
 */
const FoundingQuestion = () => {
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    const message = answer.trim();
    if (!message || sending) return;
    setSending(true);
    try {
      await api.post("/feedback", {
        message,
        category: "problem",
        page: "/founding/welcome",
        client: "web",
      });
      setSent(true);
    } catch (err) {
      // Somebody wrote this. Clearing the box on a network error would throw
      // away the one thing we asked for, with nothing to retry from.
      toast.error(getErrorMessage(err, "We couldn't send that just now."));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p
        data-testid="founding-question-sent"
        className="mt-3 flex items-start gap-2.5 font-sans text-sm text-[var(--text-secondary)]"
      >
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent-yellow-text)]" />
        Thank you — that goes straight to the people building this.
      </p>
    );
  }

  return (
    <>
      <p className="mt-2 font-sans text-sm leading-relaxed text-[var(--text-secondary)]">
        One line is plenty, and skipping it is fine — you can always use Share feedback later.
      </p>
      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={2}
        maxLength={2000}
        data-testid="founding-question"
        className="mt-3 w-full rounded-[10px] border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3.5 py-2.5 font-sans text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow-border)]"
      />
      <button
        type="button"
        onClick={send}
        disabled={!answer.trim() || sending}
        data-testid="founding-question-send"
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--accent-yellow)] px-5 py-2 font-sans text-sm font-semibold text-[var(--text-inverse)] disabled:opacity-40"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
      </button>
    </>
  );
};

/** The founding slide: what the cohort is, and what we want back from it. */
const FoundingSlide = ({ user }) => {
  const until = formatComplimentaryUntil(user);
  return (
    <div className="text-center">
      <div className="mb-4 flex justify-center">
        <FoundingMedal size={56} />
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-yellow-text)]">Founding Ensemble</p>
      <h2 className="mt-3 font-serif text-[26px] font-light leading-tight text-[var(--text-primary)]">
        Welcome{firstName(user) ? `, ${firstName(user)}` : ""}
      </h2>
      <p className="mx-auto mt-3 max-w-md font-sans text-sm leading-relaxed text-[var(--text-secondary)]">
        We're honored to welcome you as a member of the Founding Ensemble. Everything on the paid plan is yours
        {until ? ` until ${until}` : ""}, and the Founding Ensemble badge is permanent. All we ask in return is you
        share some feedback.
      </p>

      {/* The part that is actually a request, not a perk. Stated plainly and
          early, because a cohort that does not know it is expected to speak up
          is just a group of people with a discount. */}
      <div className="mt-5 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4 text-left">
        <div className="flex items-start gap-3">
          <MessageSquare className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[var(--accent-yellow-text)]" />
          <p className="font-sans text-sm leading-relaxed text-[var(--text-secondary)]">
            There's a <strong className="text-[var(--text-primary)]">Share feedback</strong> button in the sidebar on
            every screen. Your messages are routed directly to our product development team — even if it's a
            half-formed thought, we're honored to hear your perspective.
          </p>
        </div>
      </div>

      <div className="mt-3 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4 text-left">
        <h3 className="font-sans text-sm font-semibold text-[var(--text-primary)]">
          What's the hardest part of managing your teaching life right now?
        </h3>
        <FoundingQuestion />
      </div>
    </div>
  );
};

/**
 * A step slide: one part of the loop, explained.
 *
 * No link and no button, deliberately — see the note at the top of the file.
 * The points are the informative half: a title and a sentence say what the step
 * is called, and the points say what actually happens when you do it, which is
 * what a teacher deciding whether to trust this needs to read.
 */
const StepSlide = ({ step }) => (
  <div className="text-center">
    <div className="mb-5 flex justify-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-[16px] bg-[var(--studio-raised)]">
        <step.icon className="h-6 w-6 text-[var(--accent-yellow-text)]" />
      </div>
    </div>
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-tertiary)]">{step.eyebrow}</p>
    <h2 className="mt-3 font-serif text-[26px] font-light leading-tight text-[var(--text-primary)]">{step.title}</h2>
    <p className="mx-auto mt-3 max-w-md font-sans text-sm leading-relaxed text-[var(--text-secondary)]">{step.body}</p>

    <ul data-testid="welcome-points" className="mx-auto mt-5 flex max-w-md flex-col gap-3 text-left">
      {step.points.map((point) => (
        <li
          key={point}
          className="flex items-start gap-3 font-sans text-[13px] leading-relaxed text-[var(--text-secondary)]"
        >
          <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[var(--accent-yellow)]" />
          {point}
        </li>
      ))}
    </ul>
  </div>
);

/**
 * The closing slide, and the only one that acts.
 *
 * It points back at the first step rather than the last, because the walkthrough
 * ends where the work begins. The button lives in the footer with Next rather
 * than in the slide body: after four presses the reader's attention is already
 * there, and a second primary in the body would make one action look like two.
 */
const ReadySlide = () => (
  <div className="text-center">
    <div className="mb-5 flex justify-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-[16px] bg-[var(--accent-yellow-dim)]">
        <Sparkles className="h-6 w-6 text-[var(--accent-yellow-text)]" />
      </div>
    </div>
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-tertiary)]">That's the loop</p>
    <h2 className="mt-3 font-serif text-[26px] font-light leading-tight text-[var(--text-primary)]">
      Ready when you are
    </h2>
    <p className="mx-auto mt-3 max-w-md font-sans text-sm leading-relaxed text-[var(--text-secondary)]">
      Add a student, teach the lesson, send the notes. Everything else in Hans hangs off those three, and you can be
      recording about a minute from now.
    </p>
    <p className="mx-auto mt-4 max-w-md font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
      Start with one student — the rest of your roster can wait until you've seen a summary come back.
    </p>
  </div>
);

/**
 * The loop a new teacher needs, in the order they'll live it.
 *
 * The schedule step follows the same build flag the nav does, so this never
 * describes a screen that isn't there — and because no eyebrow states an
 * ordinal and the footer counts `slides.length`, dropping it can't leave the
 * copy promising a step that isn't rendered.
 */
const STEPS = [
  {
    id: "students",
    icon: UserPlus,
    eyebrow: "Your roster",
    title: "Add your students",
    body: "A student is a name, the family's email, and an age group. That's the whole form.",
    points: [
      "Hans emails the family to ask for recording consent, in wording written for the law that covers minors — you don't have to draft it or chase it.",
      "Recording stays locked for that student until the family accepts, and the status sits next to their name so you always know where you stand.",
      "Add the whole roster now if you like. Each family answers in their own time, and you can start with whoever answers first.",
    ],
  },
  ...(SCHEDULING_ENABLED
    ? [
        {
          id: "week",
          icon: CalendarDays,
          eyebrow: "Your week",
          title: "Put your week in",
          body: "Enter each recurring lesson once — Tuesdays at four — and Hans expands it into every week that follows.",
          points: [
            "Today's lessons wait on your dashboard, so starting the right one is a tap rather than a search.",
            "Skip or move a single week without disturbing the others. A cancelled lesson stays cancelled just that once.",
            "Every summary stays attached to the lesson it came from, which is what makes a year of history worth reading back.",
          ],
        },
      ]
    : []),
  {
    id: "lessons",
    icon: Mic,
    eyebrow: "In the lesson",
    title: "Capture what happened",
    body: "Record the lesson and let Hans listen, or type the notes yourself afterwards. Both end up in the same place.",
    points: [
      "A recording comes back as a summary, the practice items, and what the student asked about — usually within a few minutes of finishing.",
      "Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days.",
      "Everything is editable before it goes anywhere. Hans writes the first draft; what the family reads is yours.",
    ],
  },
  {
    id: "send",
    icon: Mail,
    eyebrow: "Afterwards",
    title: "Send it to the family",
    body: "One button emails the summary and the week's practice to the people who need it.",
    points: [
      "Hans picks the recipient from the student's age group, so a young child's notes always go to their parent.",
      "Preview the exact email first, or send yourself a test copy before any family sees it.",
      "Nothing is ever sent automatically. A summary leaves Hans when you decide it's ready.",
    ],
  },
];

const WelcomeCarousel = ({ open, onOpenChange }) => {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  // The founding slide is the ONLY cohort-specific thing here. Everyone else
  // starts on "Add your students" and finishes on the same closing slide.
  const slides = useMemo(
    () => [...(isFoundingMember(user) ? ["founding"] : []), ...STEPS.map((s) => s.id), "ready"],
    [user],
  );

  const last = index >= slides.length - 1;

  // Stepping reads the index from a ref, not from the render closure. Several
  // moves that land in ONE render — batched clicks, a test firing three in a
  // tick — would otherwise each compute their target from the same stale
  // `index` and collapse into a single step. Discrete clicks flush between
  // themselves so a person can't provoke it, but "advance from wherever we
  // actually are" is the honest formulation and costs nothing.
  const indexRef = useRef(0);
  const goTo = (next) => {
    const cur = indexRef.current;
    if (next === cur || next < 0 || next > slides.length - 1) return;
    indexRef.current = next;
    setDirection(next > cur ? 1 : -1);
    setIndex(next);
  };
  const step = (delta) => goTo(indexRef.current + delta);

  const close = () => onOpenChange(false);

  // The one navigation in the carousel, and it closes on the way — leaving the
  // dialog stacked over the roster it just opened would make the next click a
  // dismissal instead of the thing you came to do.
  const addStudent = () => {
    close();
    navigate(ADD_STUDENT_TO);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="welcome-carousel"
        className="max-w-xl border-[var(--studio-divider)] bg-[var(--studio-base)] p-0"
      >
        {/* Each slide carries its own heading, so the dialog's own title is for
            screen readers rather than the page. */}
        <DialogTitle className="sr-only">Welcome to Hans</DialogTitle>
        <DialogDescription className="sr-only">
          A short introduction to setting up your studio.
        </DialogDescription>

        {/* One fixed height for every slide, so the popup doesn't resize under
            the reader as they move — that reads as the page jumping rather
            than as one thing with several sides. Absolutely-positioned slides
            contribute no height of their own, so the frame has to state it:
            tall enough for the founding slide on a laptop, capped so it can't
            outgrow a short window. */}
        <div className="relative h-[clamp(380px,82vh,720px)] overflow-hidden">
          {/* The incoming slide animates; the outgoing one is just replaced.
              The two sibling flows wrap this in `AnimatePresence mode="wait"`,
              which holds the new child until the old one has finished leaving
              — so every move costs an exit plus an enter before there is
              anything to read. That is invisible in a form somebody spends a
              minute on, and sluggish on a carousel whose Next button is the
              only thing to do. Replacing the keyed child halves the wait and
              leaves no presence bookkeeping to outlive an interrupted
              transition. */}
          <motion.div
            key={slides[index]}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            transition={{ duration: 0.28, ease: "easeInOut" }}
            data-testid="welcome-slide"
            className="absolute inset-0 overflow-y-auto px-8 py-7"
          >
            {/* `min-h-full` + centring on an inner wrapper, never on the
                scroll container itself: centred flex content taller than its
                scroller has its overflowing top clipped and unreachable, which
                is exactly the founding slide on a short window. */}
            <div className="flex min-h-full flex-col justify-center">
              {slides[index] === "founding" && <FoundingSlide user={user} />}
              {slides[index] === "ready" && <ReadySlide />}
              {slides[index] !== "founding" && slides[index] !== "ready" && (
                <StepSlide step={STEPS.find((s) => s.id === slides[index])} />
              )}
            </div>
          </motion.div>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--studio-divider)] px-6 py-4">
          <button
            type="button"
            onClick={() => step(-1)}
            disabled={index === 0}
            data-testid="welcome-back"
            className="inline-flex items-center gap-1.5 font-sans text-sm text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] disabled:invisible"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>

          <div className="flex items-center gap-1.5" data-testid="welcome-dots">
            {slides.map((id, i) => (
              <button
                key={id}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
                onClick={() => goTo(i)}
                className={[
                  "h-1.5 rounded-full transition-all",
                  // The inactive dots need to be SEEN — half of what a dot rail
                  // says is how many more there are. `--studio-divider` (6%
                  // white) is a hairline token and disappears entirely here.
                  i === index ? "w-5 bg-[var(--accent-yellow)]" : "w-1.5 bg-[var(--text-quaternary)]",
                ].join(" ")}
              />
            ))}
          </div>

          <Button
            onClick={() => (last ? addStudent() : step(1))}
            data-testid="welcome-next"
            className="rounded-full bg-[var(--accent-yellow)] px-6 font-sans text-sm font-semibold text-[var(--text-inverse)]"
          >
            {last ? "Add your first student" : "Next"}
            {!last && <ArrowRight className="ml-2 h-3.5 w-3.5" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeCarousel;
